import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { User, UserAddress } from "@shared/types/UserAccountTypes.interface";
import { useGlobalSettings } from "@shared/hooks/useGlobalSettings";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchAddresses, updateAddressList } from "@shared/helpers/apis/addressBookHelper";
import { fetchUserAccount } from "@shared/helpers/apis/userAccountHelper";

interface AddressContextProps {
    addresses: UserAddress[] | null;
    refetchAddresses: () => void;
    updateAddresses: (addresses: UserAddress[]) => void;
    blank: UserAddress;
}

const AddressContext = createContext<AddressContextProps | undefined>(undefined);

export const AddressProvider = ({ children }: { children: ReactNode }) => {
    const [addresses, setAddresses] = useState<UserAddress[]>([]);
    const [userAccount, setUserAccount] = useState<User | null>(null);
    const [blankAddress, setBlankAddress] = useState<UserAddress>({
        id: undefined,
        alias: "",
        companyName: "",
        streetName: "",
        cityName: "",
        countryId: userAccount?.invoiceAddress?.countryId || "",
        countyName: "",
        stateName: "",
        postalCode: ""
    });

    const { customer } = useGlobalSettings();
    const customerNumber = customer?.customerNumber;
    const { data: fetchedAddresses, refetch: refetchAddresses } = useQuery({
        queryKey: ["addresses", customerNumber],
        queryFn: () => fetchAddresses(customerNumber),
        enabled: !!customerNumber,
    });

    useEffect(() => {
        if (fetchedAddresses) {
            setAddresses(fetchedAddresses.addresses);
        }
    }, [fetchedAddresses]);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const userAccountData = await fetchUserAccount();
                setUserAccount(userAccountData);
            } catch (error) {
                console.error("Failed to fetch data", error);
            }
        };

        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        setBlankAddress({
            id: undefined,
            alias: "",
            companyName: "",
            streetName: "",
            cityName: "",
            countryId: userAccount?.invoiceAddress?.countryId || "",
            countyName: "",
            stateName: "",
            postalCode: ""
        })
    }, [userAccount]);

    const addressesMutation = useMutation({
        mutationFn: (addresses: UserAddress[]) => updateAddressList(customerNumber, addresses),
        onSuccess: () => {
            refetchAddresses();
        },
    });

    return (
        <AddressContext.Provider
            value={{
                addresses,
                blank: blankAddress,
                refetchAddresses,
                updateAddresses: (addresses: UserAddress[]) => {
                    addressesMutation.mutateAsync(addresses)
                }
            }}
        >
            {children}
        </AddressContext.Provider>
    );
};

export const useAddress = () => {
    const context = useContext(AddressContext);
    if (!context) {
        throw new Error("useAddress must be used within a AddressProvider");
    }
    return context;
};
